import { useState } from 'react'
import { Link } from 'react-router-dom'
import { ClipboardList, Search } from 'lucide-react'
import { useWorkOrders } from '../../api/work-order'
import {
  WORK_ORDER_STATUS_LABEL,
  WORK_ORDER_STATUS_COLOR,
  URGENCY_LABEL,
  URGENCY_COLOR,
  type WorkOrderStatus,
  type UrgencyLevel,
} from '../../api/types/work-order'

type StatusFilter = WorkOrderStatus | 'ALL'

const STATUS_FILTERS: Array<{ value: StatusFilter; label: string }> = [
  { value: 'ALL', label: '全部' },
  { value: 'CREATED', label: '待接收' },
  { value: 'ACCEPTED', label: '已接收' },
  { value: 'DISPATCHED', label: '已派单' },
  { value: 'DIAGNOSING', label: '诊断中' },
  { value: 'IN_REPAIR', label: '维修中' },
  { value: 'WAITING_ACCEPTANCE', label: '待验收' },
  { value: 'COMPLETED', label: '已完成' },
  { value: 'CANCELLED', label: '已取消' },
]

// Ant Design Tag color → tailwind 样式映射
const COLOR_CLASS: Record<string, { text: string; bg: string }> = {
  default: { text: 'text-gray-600', bg: 'bg-gray-100' },
  processing: { text: 'text-blue-600', bg: 'bg-blue-50' },
  blue: { text: 'text-blue-600', bg: 'bg-blue-50' },
  orange: { text: 'text-orange-600', bg: 'bg-orange-50' },
  gold: { text: 'text-yellow-700', bg: 'bg-yellow-50' },
  purple: { text: 'text-purple-600', bg: 'bg-purple-50' },
  cyan: { text: 'text-cyan-600', bg: 'bg-cyan-50' },
  geekblue: { text: 'text-indigo-600', bg: 'bg-indigo-50' },
  success: { text: 'text-green-600', bg: 'bg-green-50' },
  warning: { text: 'text-amber-600', bg: 'bg-amber-50' },
  error: { text: 'text-red-600', bg: 'bg-red-50' },
}

function StatusBadge({ status }: { status: WorkOrderStatus }) {
  const color = WORK_ORDER_STATUS_COLOR[status] ?? 'default'
  const cls = COLOR_CLASS[color] ?? COLOR_CLASS.default
  return (
    <span className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium ${cls.text} ${cls.bg}`}>
      {WORK_ORDER_STATUS_LABEL[status] ?? status}
    </span>
  )
}

function UrgencyBadge({ level }: { level?: UrgencyLevel }) {
  if (!level) return <span className="text-gray-400 text-sm">-</span>
  const color = URGENCY_COLOR[level] ?? 'default'
  const cls = COLOR_CLASS[color] ?? COLOR_CLASS.default
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${cls.text} ${cls.bg}`}>
      {URGENCY_LABEL[level] ?? level}
    </span>
  )
}

export default function WorkOrderList() {
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('ALL')
  const [keyword, setKeyword] = useState('')
  const [inputValue, setInputValue] = useState('')
  const [page, setPage] = useState(0)

  const { data, isLoading } = useWorkOrders({
    status: statusFilter === 'ALL' ? undefined : statusFilter,
    keyword: keyword || undefined,
    page,
    size: 20,
  })

  const handleStatusChange = (s: StatusFilter) => {
    setStatusFilter(s)
    setPage(0)
  }

  const handleSearch = () => {
    setKeyword(inputValue)
    setPage(0)
  }

  return (
    <div className="p-6">
      {/* 页面头部 */}
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-gray-900">工单管理</h1>
        <p className="text-sm text-gray-500 mt-1">管理所有设备维修工单的全生命周期</p>
      </div>

      {/* 筛选栏 */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6">
        <div className="flex items-center gap-4 flex-wrap">
          {/* 状态筛选 */}
          <div className="flex gap-2 flex-wrap">
            {STATUS_FILTERS.map((sf) => (
              <button
                key={sf.value}
                onClick={() => handleStatusChange(sf.value)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition ${
                  statusFilter === sf.value
                    ? 'bg-primary-500 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {sf.label}
              </button>
            ))}
          </div>

          <div className="flex-1" />

          {/* 搜索框 */}
          <div className="relative flex items-center gap-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="工单号 / 医院 / 设备名称"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                className="w-64 pl-10 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <button
              onClick={handleSearch}
              className="px-4 py-2 bg-primary-500 text-white rounded-lg text-sm hover:bg-primary-600 transition"
            >
              搜索
            </button>
          </div>
        </div>
      </div>

      {/* 列表 */}
      <div className="bg-white rounded-xl border border-gray-200">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50">
                <th className="px-5 py-3 text-left text-xs font-medium text-gray-500">工单号</th>
                <th className="px-5 py-3 text-left text-xs font-medium text-gray-500">标题</th>
                <th className="px-5 py-3 text-left text-xs font-medium text-gray-500">医院</th>
                <th className="px-5 py-3 text-left text-xs font-medium text-gray-500">设备</th>
                <th className="px-5 py-3 text-left text-xs font-medium text-gray-500">故障描述</th>
                <th className="px-5 py-3 text-left text-xs font-medium text-gray-500">紧急程度</th>
                <th className="px-5 py-3 text-left text-xs font-medium text-gray-500">状态</th>
                <th className="px-5 py-3 text-left text-xs font-medium text-gray-500">创建时间</th>
                <th className="px-5 py-3 text-left text-xs font-medium text-gray-500">操作</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr>
                  <td colSpan={9} className="px-5 py-12 text-center text-gray-400">
                    加载中...
                  </td>
                </tr>
              )}
              {!isLoading && data?.content.map((wo) => (
                <tr key={wo.id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="px-5 py-4">
                    <Link
                      to={`/work-orders/${wo.id}`}
                      className="text-blue-600 hover:underline font-mono text-sm font-medium"
                    >
                      {wo.orderNo}
                    </Link>
                  </td>
                  <td className="px-5 py-4 text-sm text-gray-900 max-w-[160px] truncate">
                    {wo.title || '-'}
                  </td>
                  <td className="px-5 py-4 text-sm text-gray-900">{wo.hospitalName || '-'}</td>
                  <td className="px-5 py-4 text-sm text-gray-600">
                    <div>{wo.deviceName || '-'}</div>
                    {wo.deviceModel && (
                      <div className="text-xs text-gray-400">{wo.deviceModel}</div>
                    )}
                  </td>
                  <td className="px-5 py-4 text-sm text-gray-500 max-w-[200px] truncate">
                    {wo.faultDesc || '-'}
                  </td>
                  <td className="px-5 py-4">
                    <UrgencyBadge level={wo.urgencyLevel} />
                  </td>
                  <td className="px-5 py-4">
                    <StatusBadge status={wo.status} />
                  </td>
                  <td className="px-5 py-4 text-sm text-gray-500">
                    {wo.createdAt
                      ? new Date(wo.createdAt).toLocaleDateString('zh-CN')
                      : '-'}
                  </td>
                  <td className="px-5 py-4">
                    <Link
                      to={`/work-orders/${wo.id}`}
                      className="text-blue-600 hover:underline text-sm"
                    >
                      查看详情
                    </Link>
                  </td>
                </tr>
              ))}
              {!isLoading && data?.content.length === 0 && (
                <tr>
                  <td colSpan={9} className="px-5 py-16 text-center text-gray-500">
                    <div className="flex flex-col items-center gap-3">
                      <ClipboardList className="w-12 h-12 text-gray-300" />
                      <span>暂无工单数据</span>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* 分页 */}
        {data && data.totalPages > 1 && (
          <div className="flex items-center justify-between px-5 py-4 border-t border-gray-200">
            <span className="text-sm text-gray-500">共 {data.totalElements} 条记录</span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0}
                className="px-3 py-1 border border-gray-200 rounded text-sm hover:bg-gray-50 disabled:opacity-40"
              >
                上一页
              </button>
              <span className="text-sm text-gray-600">
                第 {page + 1} / {data.totalPages} 页
              </span>
              <button
                onClick={() => setPage((p) => Math.min(p + 1, data.totalPages - 1))}
                disabled={page >= data.totalPages - 1}
                className="px-3 py-1 border border-gray-200 rounded text-sm hover:bg-gray-50 disabled:opacity-40"
              >
                下一页
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
